/**
 * POS payment in-flight tracker.
 *
 * Singleton reactive store for an active "Send to POS" dispatch
 * (either an OTC tablet or a Stripe Terminal reader). Drives the
 * <pos-payment-in-progress-drawer> mounted in DashboardLayout so the
 * operator sees live status as the customer taps, regardless of which
 * page they navigate to.
 *
 * Lifecycle:
 *   - SendToPosButton.onPick → posPaymentService.start(...)
 *   - Service polls the transaction GET every 2.5s, watching for
 *     `transaction.due` → 0 (webhook ack landed) or session expiry.
 *   - Service exposes `cancel()` (aborts on Stripe + cancels session)
 *     and `dismiss()` (closes the drawer without aborting).
 *   - On `paid` terminal state, auto-dismisses after a brief
 *     confirmation flash so the operator sees success then carries on.
 *
 * Persistence:
 *   - On app boot, `rehydrate()` queries an "active sessions for me"
 *     endpoint so the drawer re-appears after page refresh while a
 *     payment is in flight.
 *
 * Multi-surface initiation:
 *   - The service is the single source of truth. Both
 *     TransactionDetails' Send to POS and BookingDrawer's Send to POS
 *     route through the same store, so initiating from one surface and
 *     navigating to another keeps the drawer alive.
 */

import Vue from 'vue';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';

const POLL_INTERVAL_MS = 2500;
const POST_SUCCESS_DISMISS_MS = 5000;
const POST_CANCEL_DISMISS_MS = 1500;

const state = Vue.observable({
  // null when nothing in flight; otherwise the session metadata + live status.
  // status ∈ 'waiting' | 'processing' | 'paid' | 'failed' | 'cancelled' | 'expired'
  activeSession: null,
  isMinimized: false,
});

let pollTimer = null;
let dismissTimer = null;
let rehydrateAttempted = false;

async function getAuthToken() {
  const data = await Auth.currentSession();
  return data.idToken.jwtToken;
}

function ctx() {
  const companyId = localStorage.getItem('companyId');
  const groupRaw = localStorage.getItem('group');
  const companyGroupId = groupRaw ? JSON.parse(groupRaw).id : null;
  return { companyId, companyGroupId };
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function clearDismissTimer() {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
}

function startPolling() {
  stopPolling();
  // Fire one poll immediately so the drawer doesn't show stale state
  // for 2.5s before the first network ack.
  _poll();
  pollTimer = setInterval(_poll, POLL_INTERVAL_MS);
}

async function _poll() {
  if (!state.activeSession) {
    stopPolling();
    return;
  }
  // Don't poll once we're in a terminal state — let the dismiss timer
  // finish naturally.
  const terminal = ['paid', 'failed', 'cancelled', 'expired'];
  if (terminal.includes(state.activeSession.status)) {
    stopPolling();
    return;
  }

  // Expiry check is purely local — no network needed.
  if (
    state.activeSession.expiresAt
    && Date.now() > new Date(state.activeSession.expiresAt).getTime()
  ) {
    state.activeSession = { ...state.activeSession, status: 'expired' };
    stopPolling();
    return;
  }

  const { companyId, companyGroupId } = ctx();
  if (!companyId || !companyGroupId) return;
  try {
    const token = await getAuthToken();
    const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/transactions/${state.activeSession.transactionId}`;
    const res = await axios.get(url, { headers: { Authorization: token } });
    const tx = res.data?.transaction;
    if (!tx) return;
    // Capture the human order number for display — the session only carries
    // the transaction id, but the drawer should show the order number staff
    // recognize. Set once, the first time the poll sees it.
    if (tx.order_number != null && state.activeSession && state.activeSession.orderNumber == null) {
      state.activeSession = { ...state.activeSession, orderNumber: tx.order_number };
    }
    // due ≤ ~0 = fully paid (snap small float residue).
    if (Number(tx.due) <= 0.005) {
      state.activeSession = { ...state.activeSession, status: 'paid', paidAt: Date.now() };
      stopPolling();
      clearDismissTimer();
      dismissTimer = setTimeout(() => {
        if (state.activeSession && state.activeSession.status === 'paid') {
          state.activeSession = null;
          state.isMinimized = false;
        }
      }, POST_SUCCESS_DISMISS_MS);
    }
  } catch (err) {
    // Quiet failure — polling is best-effort. Network blips are common.
    // eslint-disable-next-line no-console
    console.warn('posPayment poll failed', err?.message);
  }
}

export default {
  state,

  /**
   * Begin tracking a freshly dispatched POS session.
   * @param {object} session
   *   - session_hash, payment_intent_id (nullable for tablets),
   *     transaction_id, device_id, device_type, device_name,
   *     amount, expires_at
   */
  start(session) {
    clearDismissTimer();
    state.activeSession = {
      id: session.session_id,
      hash: session.session_hash,
      paymentIntentId: session.payment_intent_id || null,
      transactionId: session.transaction_id,
      orderNumber: session.order_number || null,
      deviceId: session.device_id,
      readerId: session.reader_id || null,
      deviceType: session.device_type || 'otc_pos_tablet',
      deviceName: session.device_name || 'POS device',
      amount: session.amount || null,
      expiresAt: session.expires_at || null,
      startedAt: Date.now(),
      status: 'waiting',
    };
    state.isMinimized = false;
    startPolling();
  },

  /**
   * Dispatch a transaction to a POS device and start tracking it. The one
   * dispatch path for every surface (SendToPosButton, gift card purchase
   * drawer). Throws the axios error on failure so the caller owns the message.
   * @param {{ transactionId: number, device: { id, name, device_type }, amount: number|null }} args
   * @returns the send-to-pos response body.
   */
  async sendToDevice({ transactionId, device, amount }) {
    const { companyId, companyGroupId } = ctx();
    const token = await getAuthToken();
    const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/transactions/${transactionId}/send-to-pos`;
    // A registered Stripe reader is dispatched by reader_id; an OTC POS app
    // device by device_id. The list lambda gives readers a string id
    // ('reader-7') and a numeric reader_id so the two can never be mixed up.
    const isReader = device.device_type === 'stripe_terminal_reader';
    const payload = isReader ? { reader_id: device.reader_id } : { device_id: device.id };
    const res = await axios.post(url, payload, { headers: { Authorization: token } });
    this.start({
      session_id: res.data?.session_id,
      session_hash: res.data?.session_hash,
      payment_intent_id: res.data?.payment_intent_id || null,
      transaction_id: transactionId,
      device_id: isReader ? null : device.id,
      reader_id: isReader ? device.reader_id : null,
      device_type: device.device_type,
      device_name: device.name,
      amount,
      expires_at: res.data?.expires_at,
    });
    return res.data || {};
  },

  minimize() {
    state.isMinimized = true;
  },

  expand() {
    state.isMinimized = false;
  },

  /**
   * Abort the in-flight session. Calls the cancel-pos-session lambda
   * which aborts the action on the reader (if reader) + cancels the
   * PaymentIntent (if any) + marks the session cancelled.
   */
  async cancel() {
    if (!state.activeSession) return;
    const { companyId, companyGroupId } = ctx();
    if (!companyId || !companyGroupId) return;
    const id = state.activeSession.id;
    if (!id) {
      // No id means we have nothing the cancel lambda can identify by
      // — just clear locally.
      state.activeSession = { ...state.activeSession, status: 'cancelled' };
      stopPolling();
      clearDismissTimer();
      dismissTimer = setTimeout(() => {
        state.activeSession = null;
        state.isMinimized = false;
      }, POST_CANCEL_DISMISS_MS);
      return;
    }
    let res = null;
    try {
      const token = await getAuthToken();
      res = await axios.delete(
        `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/pos-sessions/${id}`,
        { headers: { Authorization: token } },
      );
    } catch (err) {
      // The server refused (409: tap in progress, or the payment is being
      // recorded) or never answered (network, 5xx). Either way nothing was
      // cancelled on the device or the reader, so the session stays and
      // keeps polling; the drawer shows the error.
      // eslint-disable-next-line no-console
      console.warn('posPayment cancel failed', err?.message);
      throw err;
    }
    stopPolling();
    clearDismissTimer();
    if (res?.data?.already_paid === true && state.activeSession) {
      // The customer tapped before the cancel reached the reader: the payment
      // is recorded, so the drawer shows it as received, not cancelled.
      state.activeSession = { ...state.activeSession, status: 'paid', paidAt: Date.now() };
      dismissTimer = setTimeout(() => {
        if (state.activeSession && state.activeSession.status === 'paid') {
          state.activeSession = null;
          state.isMinimized = false;
        }
      }, POST_SUCCESS_DISMISS_MS);
      return;
    }
    if (state.activeSession) {
      state.activeSession = { ...state.activeSession, status: 'cancelled' };
    }
    dismissTimer = setTimeout(() => {
      state.activeSession = null;
      state.isMinimized = false;
    }, POST_CANCEL_DISMISS_MS);
  },

  /**
   * Close the drawer without aborting. Use after a paid/cancelled
   * confirmation, or for the operator to dismiss a stuck state.
   */
  dismiss() {
    stopPolling();
    clearDismissTimer();
    state.activeSession = null;
    state.isMinimized = false;
  },

  /**
   * Restore drawer state after page refresh. Called from App or
   * DashboardLayout on mount. Idempotent — safe to call multiple
   * times across navigations.
   */
  async rehydrate() {
    if (rehydrateAttempted || state.activeSession) return;
    rehydrateAttempted = true;
    const { companyId, companyGroupId } = ctx();
    if (!companyId || !companyGroupId) return;
    try {
      const token = await getAuthToken();
      const res = await axios.get(
        `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/pos-sessions`,
        { headers: { Authorization: token } },
      );
      const sessions = res.data?.sessions || [];
      if (sessions.length > 0) {
        const s = sessions[0]; // most recent
        state.activeSession = {
          id: s.id,
          hash: s.session_hash,
          paymentIntentId: s.payment_intent_id || null,
          transactionId: s.transaction_id,
          orderNumber: s.order_number || null,
          deviceId: s.device_id,
          readerId: s.reader_id || null,
          deviceType: s.device_type || 'otc_pos_tablet',
          deviceName: s.device_name || 'POS device',
          amount: s.amount || null,
          expiresAt: s.expires_at || null,
          startedAt: s.created_at ? new Date(s.created_at).getTime() : Date.now(),
          status: 'waiting',
        };
        // After refresh, default to minimized so operator's current
        // task isn't visually interrupted; they can expand on click.
        state.isMinimized = true;
        startPolling();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('posPayment rehydrate failed', err?.message);
    }
  },
};
